import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Search } from "../../../components/CRUDcomponents/Actionbar/Modals/Search/Search";
import { Print } from "../../../components/CRUDcomponents/Actionbar/Modals/Print/Print";
import DynamicTable from "../../../components/Table/IndentTable";
import Header from "../../../components/Header/Header";
import ActionBar from "../../../components/CRUDcomponents/Actionbar/ActionBar";
import Footer from "../../../components/Footer/Footer";
import axios from "axios";
import { useSelector } from "react-redux";

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;
const apiEndpoint = `${API_BASE_URL}/popage/indent`;

const IRIndent = () => {
  const [indents, setIndents] = useState([]);
  const [filteredIndents, setFilteredIndents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedRow, setSelectedRow] = useState(null);
  const [searchQuery, setSearchQuery] = useState("");

  const soid = useSelector((state) => state.auth.id);
  const role = useSelector((state) => state.auth.role);
  
  // Debug logging for soid and role
  useEffect(() => {
    console.log("Indent4 Debug - soid:", soid, "role:", role);
  }, [soid, role]);
  const [dropdownData, setDropdownData] = useState({
    users: [],
    projects: [],
    stages: [],
    types: [],
  });

  const navigate = useNavigate();

  const getApiEndpoint = () => {
    // Convert role to lowercase for consistent comparison
    const lowerCaseRole = role.toLowerCase();

    // Check if the role is admin or store head
    if (lowerCaseRole === "admin" || lowerCaseRole === "store head") {
      return `${API_BASE_URL}/adminpage/soindent`;
    }

    // For other roles, use the regular endpoint
    return `${API_BASE_URL}/adminpage/pesoindent`;
  };

  const getEditFields = () => [
    {
      name: "ProjectID",
      label: "Project",
      type: "select",
      options:
        dropdownData.projects.map((project) => ({
          value: project.id,
          label: project.name,
        })) || [],
    },
    {
      name: "TypeID",
      label: "Type",
      type: "select",
      options:
        dropdownData.types.map((type) => ({
          value: type.id,
          label: type.name,
        })) || [],
    },
  ];

  const formatCreationDate = (dateStr) => {
    if (!dateStr) return "N/A";
    if (/^\d{2}[\/-]\d{2}[\/-]\d{2,4}$/.test(dateStr)) return dateStr;
    const date = new Date(dateStr);
    if (isNaN(date.getTime())) return "N/A";
    const day = String(date.getDate()).padStart(2, '0');
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const year = String(date.getFullYear()).slice(-2);
    return `${day}/${month}/${year}`;
  };

  const fetchIndents = async () => {
    setLoading(true);
    setError(null);
    try {
      const apiEndpoint = getApiEndpoint();
      const params = role.toLowerCase() !== "admin" ? { params: { soid } } : {};

      console.log("Fetching indents with:", {
        endpoint: apiEndpoint,
        role,
        soid,
        params,
      });

      const response = await axios.get(apiEndpoint, params);

      console.log("API Response:", {
        status: response.status,
        dataLength: response.data?.length,
        data: response.data
      });

      if (!response.data) {
        throw new Error("No data received from server");
      }

      const formattedIndents = response.data
        .map((indent) => ({
          ...indent,
          L1: indent.L1 ? "Approved" : "Pending",
          L2: indent.L2 ? "Approved" : "Pending",
          PO: indent.PO ? "Approved" : "Pending",
          PH: indent.PH ? "Approved" : "Pending",
          IR: indent.IR ? "Received" : "Pending",
          SH: indent.SH ? "Received" : "Pending",
          CreationDate: formatCreationDate(indent.CreationDate),
        }))
        .sort((a, b) => new Date(b.CreationDate) - new Date(a.CreationDate));

      console.log("Processed indents:", formattedIndents);

      setIndents(formattedIndents);
      setFilteredIndents(formattedIndents);
    } catch (error) {
      console.error("Error fetching indents:", {
        message: error.message,
        response: error.response?.data,
        status: error.response?.status,
        role,
        soid,
      });
      setError("Failed to fetch indents. Please try again later.");
    } finally {
      setLoading(false);
    }
  };

  const fetchDropdownData = async () => {
    try {
      const [usersRes, projectsRes, stagesRes, typesRes] = await Promise.all([
        axios.get(`${API_BASE_URL}/dropdown/users`),
        axios.get(`${API_BASE_URL}/dropdown/mprojects`),
        axios.get(`${API_BASE_URL}/dropdown/mstages`),
        axios.get(`${API_BASE_URL}/dropdown/mindenttypes`),
      ]);

      setDropdownData({
        users: usersRes.data.map((user) => ({
          id: user.UserID,
          name: user.Username,
        })),
        projects: projectsRes.data.map((project) => ({
          id: project.ProjectID,
          name: project.ProjectName,
        })),
        stages: stagesRes.data.map((stage) => ({
          id: stage.StageID,
          name: stage.StageName,
        })),
        types: typesRes.data.map((type) => ({
          id: type.TypeID,
          name: type.TypeName,
        })),
      });
    } catch (error) {
      console.error("Error fetching dropdown data:", error);
      setError("Failed to fetch dropdown data. Please try again later.");
    }
  };

  useEffect(() => {
    fetchIndents();
    fetchDropdownData();
  }, [role, soid]);

  const handleSearch = (query) => {
    setSearchQuery(query);
    if (!query || query.trim() === '') {
      setFilteredIndents(indents);
      return;
    }
    
    const searchTerm = query.toLowerCase().trim();
      const filtered = indents.filter((indent) => {
      const searchableFields = [
        indent.IndentID?.toString(),
        indent.ProjectName,
        indent.TypeName,
        indent.Status,
        indent.CreationDate,
        indent.TaskSchedule,
        indent.L1,
        indent.L2,
        indent.PO,
        indent.PH,
        indent.IR,
        indent.SH
      ];
      
      return searchableFields.some(field => 
        field && field.toString().toLowerCase().includes(searchTerm)
        );
      });
    
      setFilteredIndents(filtered);
  };

  const handleDateFilter = (filterOptions) => {
    const { dateFrom, dateTo } = filterOptions;
    if (dateFrom && dateTo) {
      const filtered = indents.filter((indent) => {
        const creationDate = new Date(indent.CreationDate);
        return (
          creationDate >= new Date(dateFrom) && creationDate <= new Date(dateTo)
        );
      });
      setFilteredIndents(
        filtered.sort(
          (a, b) => new Date(b.CreationDate) - new Date(a.CreationDate)
        )
      );
    } else {
      setFilteredIndents(indents);
    }
  };

  // Button click handlers for redirection
  const handleRedirect = (page) => {
    navigate(page);
  };

  const handleRowClick = (row) => {
    try {
      if (row && row.SH !== undefined) {
        if (row.SH === "Received") {
          navigate("/IRSHtable", { state: { row } }); // Ensure row is passed
        } else if (row.SH === "Pending") {
          navigate("/IRtable", { state: { row } }); // Ensure row is passed
        }
      } else {
        console.error("Invalid row data:", row);
      }
    } catch (error) {
      console.error("Error in handleRowClick:", error);
    }
  };

  // Add displayData for consistent filtering
  const displayData = React.useMemo(() => {
    if (!filteredIndents) return [];
    return filteredIndents;
  }, [filteredIndents]);

  return (
    <div className="min-h-screen flex flex-col">
      <Header />
      <div className="flex-grow p-4">
        <div className="mt-6 mb-2 text-center">
          <h3 className="text-xl font-semibold">Store Officer Page</h3>
        </div>

        {loading && <div className="text-center py-4">Loading...</div>}
        {error && <div className="text-red-500 text-center py-4">{error}</div>}

        {!loading && !error && (
          <>
            {dropdownData.users.length > 0 &&
              dropdownData.projects.length > 0 && (
                <ActionBar
                  title="Indent"
                  fields={getEditFields()}
                  apiEndpoint={apiEndpoint}
                  data={displayData}
                  selectedRow={selectedRow}
                  onRowSelect={setSelectedRow}
                  onRefresh={fetchIndents}
                  onSearch={handleSearch}
                  onFilter={handleDateFilter}
                  rowKey="IndentID"
                  userRole={role}
                  addLink="/addl2Indent"
                  editRoute="/IRtable"
                  searchQuery={searchQuery}
                />
              )}

            <div className="mt-4 overflow-x-auto">
              <DynamicTable
                columns={[
                  { key: "IndentID", label: "Indent ID" },
                  { key: "CreationDate", label: "Creation Date" },
                  { key: "ProjectName", label: "Project" },
                  { key: "TypeName", label: "Indent Type" },
                  { key: "TaskSchedule", label: "Task Scheduled" },
                  { key: "Status", label: "Status" },
                  { key: "ItemCount", label: "Item Count" },
                ]}
                data={displayData}
                selectedRow={selectedRow}
                onRowSelect={setSelectedRow}
                onDoubleClick={handleRowClick}
                rowKey="IndentID"
                userIdKey="UserID"
                highlightColor="bg-green-100"
                noDataMessage="No indents found."
                userRole={role}
              />
            </div>
          </>
        )}
      </div>
      <Footer />
    </div>
  );
};

export default IRIndent;
